import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import CommandCenterHeader from '../components/command-center/CommandCenterHeader';
import CommandCenterHero from '../components/command-center/CommandCenterHero';
import CommandCenterSearch from '../components/command-center/CommandCenterSearch';
import RecommendedTools from '../components/command-center/RecommendedTools';
import ToolCategory from '../components/command-center/ToolCategory';
import ContinueWorkSection from '../components/command-center/ContinueWorkSection';
import QuickHelpPanel from '../components/command-center/QuickHelpPanel';
import CommandCenterSkeleton from '../components/command-center/CommandCenterSkeleton';
import CommandCenterEmptyState from '../components/command-center/CommandCenterEmptyState';
import useContinueWork from '../components/command-center/useContinueWork';
import { commandCenterStyles } from '../components/command-center/styles';

import { commandCenterTools, CATEGORIES } from '../config/commandCenterTools';
import { pickRecommendedTools } from '../utils/prioritizeTools';

/* ─────────────────────────────────────────────────────────────────────── */

function safeMatch(tool, q) {
  const hay = [
    tool.title, tool.description, tool.category, tool.actionLabel,
    ...(tool.keywords || []),
  ].join(' ').toLowerCase();
  return hay.includes(q);
}

// If /api/analytics/events isn't wired on this env we short-circuit
// after the first failure so we don't spam the console.
let _analyticsDisabled = false;

function trackEvent(name, payload = {}) {
  if (_analyticsDisabled) return;
  try {
    const api = process.env.REACT_APP_BACKEND_URL;
    const t = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
    fetch(`${api}/api/analytics/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      },
      body: JSON.stringify({
        sessionId: sessionStorage.getItem('as_session_id') || 'home',
        eventType: name,
        feature: 'home_command_center',
        metadata: payload,
      }),
      keepalive: true,
    })
      .then((r) => { if (r && (r.status === 404 || r.status === 405)) _analyticsDisabled = true; })
      .catch(() => { _analyticsDisabled = true; });
  } catch { _analyticsDisabled = true; }
}

/* ─────────────────────────────────────────────────────────────────────── */

/**
 * HomeCommandCenter — mounted at /command-center.
 * Redesigned per spec (Feb 2026) to visually mirror the AskStewie
 * Landing page and route stewards to the right tool for their situation.
 */
export default function HomeCommandCenter({ user, onLogout }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const continueItems = useContinueWork();

  // "Loading" is a soft delay so skeletons don't flash on already-fast
  // hydration; matches the same feel the rest of the app uses.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) setLoading(false); }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  // Fire page-view telemetry once.
  useEffect(() => {
    trackEvent('command_center_viewed', {});
  }, []);

  // Build the prioritization context from real data (no fake signals).
  const ctx = useMemo(() => ({
    hasOpenCase:       continueItems.some((i) => i.type === 'case'),
    hasDraftGrievance: continueItems.some((i) => i.type === 'grievance'),
  }), [continueItems]);

  const recommended = useMemo(() => pickRecommendedTools(ctx, 4), [ctx]);

  const q = query.trim().toLowerCase();
  const isFiltering = q.length > 0;

  const availableTools = commandCenterTools.filter((t) => t.available !== false);
  const filtered = isFiltering ? availableTools.filter((t) => safeMatch(t, q)) : availableTools;

  const byCategory = useMemo(() => {
    const m = {};
    filtered.forEach((t) => {
      if (!m[t.category]) m[t.category] = [];
      m[t.category].push(t);
    });
    // Sort within each bucket by descending base priority for a stable order.
    Object.values(m).forEach((arr) => arr.sort((a, b) => (b.priority || 0) - (a.priority || 0)));
    return m;
  }, [filtered]);

  const handleSelect = useCallback((tool) => {
    trackEvent('command_center_tool_clicked', { tool_id: tool.id, from_search: isFiltering });
    navigate(tool.route);
  }, [navigate, isFiltering]);

  const handleRecommended = useCallback((tool) => {
    trackEvent('command_center_recommended_tool_clicked', { tool_id: tool.id });
    navigate(tool.route);
  }, [navigate]);

  const handleQueryChange = useCallback((v) => {
    setQuery(v);
    if (v.trim().length >= 2) trackEvent('command_center_search', { qLen: v.length });
  }, []);

  const showNoResults = isFiltering && filtered.length === 0;

  return (
    <div className="cc-root" data-testid="command-center">
      <style>{commandCenterStyles}</style>

      <CommandCenterHeader user={user} onLogout={onLogout} />

      <main className="cc-main">
        <CommandCenterHero
          user={user}
          primaryTool={recommended[0]}
          onPrimaryAction={handleRecommended}
        />

        <CommandCenterSearch
          value={query}
          onChange={handleQueryChange}
          resultCount={filtered.length}
          isFiltering={isFiltering}
        />

        {loading && !isFiltering && <CommandCenterSkeleton />}

        {loadError && !loading && (
          <CommandCenterEmptyState variant="error" onRetry={() => window.location.reload()} />
        )}

        {!loading && !loadError && showNoResults && (
          <CommandCenterEmptyState variant="no-results" onClear={() => setQuery('')} />
        )}

        {!loading && !loadError && !isFiltering && continueItems.length > 0 && (
          <ContinueWorkSection items={continueItems} />
        )}

        {!loading && !loadError && !isFiltering && (
          <RecommendedTools tools={recommended} onSelect={handleRecommended} />
        )}

        {!loading && !loadError && !showNoResults && CATEGORIES.map((cat) => (
          <ToolCategory
            key={cat.id}
            category={cat}
            tools={byCategory[cat.id] || []}
            onSelect={handleSelect}
          />
        ))}

        {!loading && !loadError && !isFiltering && <QuickHelpPanel />}
      </main>
    </div>
  );
}
